import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import AppShell from "../components/AppShell";
import GetHelpSection from "../components/GetHelpSection";
import { useLocalAgentId } from "../hooks/useLocalAgentId";
import { BlockedIcon, InfoIcon, WarningIcon } from "../components/Icons";
import { StatusDot, StatusPill } from "../components/StatusDot";
import {
  getInstallerInfo,
  listAgents,
  resendAgentSetup,
  type AgentRow,
  type InstallerInfo,
} from "../api/agents";
import { listShares, deleteShare, type ShareRow } from "../api/shares";
import { listPrinters, sharePrinter, unsharePrinter, type PrinterRow } from "../api/printers";
import { formatRelative } from "../utils/format";
import { isHostUnavailable } from "../utils/agent";

const POLL_MS = 30_000;
const PROVISIONING_POLL_MS = 5_000;

export default function ComputerDetail() {
  const { agentId } = useParams<{ agentId: string }>();

  const localAgentId = useLocalAgentId();
  const [agent, setAgent] = useState<AgentRow | null | "not_found">(null);
  const [shares, setShares] = useState<ShareRow[] | null>(null);
  const [printers, setPrinters] = useState<PrinterRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyShare, setBusyShare] = useState<string | null>(null);
  const [busyPrinter, setBusyPrinter] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendCode, setResendCode] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [installerInfo, setInstallerInfo] = useState<InstallerInfo | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  async function fetchAll() {
    try {
      const [agentData, shareData, printerData] = await Promise.all([
        listAgents(),
        listShares(),
        listPrinters(),
      ]);
      if (!mountedRef.current) return;
      const found = agentData.results.find((a) => a.id === agentId) ?? "not_found";
      setAgent(found);
      if (found !== "not_found") {
        setShares(shareData.results.filter((s) => s.host_agent === agentId));
        setPrinters(printerData.results.filter((p) => p.host_agent === agentId));
      }
      setError(null);
    } catch {
      if (mountedRef.current) setError("Couldn't load computer details. We'll try again shortly.");
    }
  }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // Fetch installer metadata once so the "Update available" pill and the
  // offline reinstall link can show the current version. Silent on failure —
  // if metadata is unavailable we fall back to the static download URL.
  useEffect(() => {
    let cancelled = false;
    getInstallerInfo()
      .then((info) => { if (!cancelled && mountedRef.current) setInstallerInfo(info); })
      .catch(() => { /* non-fatal: hide version, keep generic label */ });
    return () => { cancelled = true; };
  }, []);

  // Poll faster while the agent is still provisioning so the user sees the
  // "Setting up → Online" transition within seconds of landing here from the
  // confirm flow. Drop back to 30s once it's any terminal status.
  useEffect(() => {
    const isProvisioning =
      agent !== null && agent !== "not_found" && agent.status === "provisioning";
    const interval = isProvisioning ? PROVISIONING_POLL_MS : POLL_MS;
    const id = setInterval(fetchAll, interval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, agent]);

  async function onResendSetup() {
    if (!agentId) return;
    setResending(true);
    setResendError(null);
    try {
      const result = await resendAgentSetup(agentId);
      if (mountedRef.current) setResendCode(result.code);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      if (mountedRef.current) {
        setResendError(
          e?.response?.data?.detail ?? "Couldn't generate a new setup code. Please try again.",
        );
      }
    } finally {
      if (mountedRef.current) setResending(false);
    }
  }

  async function onDeleteShare(share: ShareRow) {
    if (
      !window.confirm(
        `Stop sharing "${share.label || share.share_name}"? Anyone using this folder will lose access.`,
      )
    )
      return;
    setBusyShare(share.id);
    try {
      await deleteShare(share.id);
      await fetchAll();
    } catch {
      if (mountedRef.current) setError("Couldn't stop sharing. Please try again.");
    } finally {
      if (mountedRef.current) setBusyShare(null);
    }
  }

  async function onSharePrinter(printer: PrinterRow) {
    setBusyPrinter(printer.id);
    try {
      await sharePrinter(printer.id);
      await fetchAll();
    } catch {
      if (mountedRef.current) setError("Couldn't share this printer. Please try again.");
    } finally {
      if (mountedRef.current) setBusyPrinter(null);
    }
  }

  async function onUnsharePrinter(printer: PrinterRow) {
    if (!window.confirm("Stop sharing this printer? Other computers will lose access.")) return;
    setBusyPrinter(printer.id);
    try {
      await unsharePrinter(printer.id);
      await fetchAll();
    } catch {
      if (mountedRef.current) setError("Couldn't stop sharing. Please try again.");
    } finally {
      if (mountedRef.current) setBusyPrinter(null);
    }
  }

  if (agent === "not_found") {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl">
          <Link
            to="/dashboard"
            className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
          >
            <ChevronLeft /> Back to Overview
          </Link>
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="text-slate-500">This computer wasn't found.</p>
            <Link
              to="/dashboard"
              className="no-tap-highlight mt-4 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Back to Overview
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  // Only truly unavailable for resource access: offline or blocked.
  // needs_attention means KraetdNet has issues but the PC is still on —
  // Windows shares may still work.
  const hostUnavailable = agent !== null && isHostUnavailable(agent);

  const activeShares = shares?.filter((s) => s.status !== "removed") ?? [];
  const visiblePrinters = printers?.filter((p) => p.status !== "removed") ?? [];

  // Narrow once so JSX further down can rely on a plain AgentRow.
  // Use typeof to dodge a TS quirk where `agent !== "not_found"` is
  // flagged as "no overlap" once earlier narrowing has stripped the
  // literal from the inferred type.
  const resolvedAgent: AgentRow | null =
    agent !== null && typeof agent !== "string" ? agent : null;

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl space-y-8">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
        >
          <ChevronLeft /> Back to Overview
        </Link>

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        )}

        {/* Computer header */}
        {agent === null ? (
          <div className="space-y-3">
            <div className="h-7 w-1/3 animate-pulse rounded bg-slate-200" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-start gap-4">
              <div className="mt-1 shrink-0">
                <StatusDot status={agent.status} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <h1 className="text-xl font-semibold text-slate-900">
                    {agent.label || agent.hostname}
                  </h1>
                  <div className="flex shrink-0 items-center gap-2">
                    <Link
                      to={`/computers/${agent.id}/add-printer`}
                      className="no-tap-highlight rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Add a printer
                    </Link>
                    {agent.update_available && (
                      <a
                        href={installerInfo?.download_url ?? "/api/v1/agents/installer/download"}
                        title={
                          installerInfo?.version
                            ? `Download installer v${installerInfo.version}`
                            : "Download installer"
                        }
                        className="no-tap-highlight rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700 hover:bg-sky-200"
                      >
                        Update available
                      </a>
                    )}
                    {agentId === localAgentId && localAgentId !== null && (
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                        This computer
                      </span>
                    )}
                    <StatusPill status={agent.status} />
                  </div>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {agent.os} {agent.os_version}
                  {agent.last_seen_at && (
                    <> · last seen {formatRelative(agent.last_seen_at)}</>
                  )}
                </p>
                {agent.tailscale_ip && agent.status === "online" && (
                  <div className="mt-2 inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-500">
                    <span className="font-mono">{agent.tailscale_ip}</span>
                  </div>
                )}

                {agent.status === "offline" && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                    <WarningIcon />
                    <div>
                      <span className="font-medium">Can't connect</span> — this computer is currently off.
                      <br />
                      <span className="text-amber-700">
                        Turn it on to access its shared files and printers.
                      </span>
                      <br />
                      <a
                        href={installerInfo?.download_url ?? "/api/v1/agents/installer/download"}
                        className="no-tap-highlight mt-1 inline-block text-xs font-medium text-amber-900 underline hover:text-amber-700"
                      >
                        Need to reinstall on this PC? Download installer
                        {installerInfo?.version ? ` (v${installerInfo.version})` : ""}
                      </a>
                    </div>
                  </div>
                )}
                {agent.status === "blocked" && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-orange-50 px-3 py-2.5 text-sm text-orange-800">
                    <BlockedIcon />
                    <div>
                      <span className="font-medium">Blocked</span> — this computer can't connect to
                      the office network.{" "}
                      <Link to="/computers" className="underline">
                        Unblock it from the Computers page.
                      </Link>
                    </div>
                  </div>
                )}
                {agent.status === "needs_attention" && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-800">
                    <InfoIcon />
                    <div>
                      <span className="font-medium">Needs attention</span> — KraetdNet may not be
                      running correctly. Try restarting this computer.
                    </div>
                  </div>
                )}
                {agent.status === "provisioning" && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Setting up — this usually takes less than a minute.
                    </div>
                    {agent.last_heartbeat_error && (
                      <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <p className="font-medium">Last error from this computer:</p>
                        <p className="mt-0.5 font-mono break-all">{agent.last_heartbeat_error}</p>
                      </div>
                    )}
                    {resendCode ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                        <p className="font-medium">New setup code generated</p>
                        <p className="mt-1 font-mono text-base font-semibold tracking-wider">
                          {resendCode}
                        </p>
                        <p className="mt-2 text-xs">
                          On this PC, open KraetdNet and enter this code, or re-run the installer.
                          The old entry will keep showing here — once the new one comes online, you
                          can remove this one.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <button
                          type="button"
                          onClick={onResendSetup}
                          disabled={resending}
                          className="no-tap-highlight rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                        >
                          {resending ? "Generating…" : "Resend Setup"}
                        </button>
                        <p className="mt-1 text-xs text-slate-500">
                          Stuck here? Generate a fresh setup code for this PC.
                        </p>
                      </div>
                    )}
                    {resendError && (
                      <p role="alert" className="text-xs text-red-700">
                        {resendError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Remote help */}
        {agentId && resolvedAgent && resolvedAgent.status !== "uninstalled" ? (
          <GetHelpSection agentId={agentId} disabled={hostUnavailable} />
        ) : null}

        {/* Shared folders */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Shared Folders
            </h2>
            <Link
              to="/sharing/add"
              className="no-tap-highlight text-xs font-medium text-slate-500 underline hover:text-slate-900"
            >
              Share a folder
            </Link>
          </div>

          {shares === null ? (
            <SkeletonList count={2} />
          ) : activeShares.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center">
              <p className="text-sm text-slate-500">No shared folders on this computer.</p>
              <Link
                to="/sharing/add"
                className="no-tap-highlight mt-3 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
              >
                Share a folder
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {activeShares.map((share) => (
                <ShareCard
                  key={share.id}
                  share={share}
                  hostUnavailable={hostUnavailable}
                  busy={busyShare === share.id}
                  onDelete={() => onDeleteShare(share)}
                />
              ))}
            </ul>
          )}
        </section>

        {/* Printers */}
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Printers
          </h2>

          {printers === null ? (
            <SkeletonList count={1} />
          ) : visiblePrinters.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center">
              <p className="text-sm text-slate-500">
                No printers detected on this computer.
                <br />
                Plug one in — it'll appear here within a minute.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {visiblePrinters.map((printer) => (
                <PrinterCard
                  key={printer.id}
                  printer={printer}
                  hostUnavailable={hostUnavailable}
                  busy={busyPrinter === printer.id}
                  onShare={() => onSharePrinter(printer)}
                  onUnshare={() => onUnsharePrinter(printer)}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function ShareCard({
  share,
  hostUnavailable,
  busy,
  onDelete,
}: {
  share: ShareRow;
  hostUnavailable: boolean;
  busy: boolean;
  onDelete: () => void;
}) {
  const accessLabel =
    share.permissions.some((p) => p.access === "write") ? "Read and write" : "Read only";

  return (
    <li className={`rounded-2xl border border-slate-200 bg-white p-5 ${hostUnavailable ? "opacity-70" : ""}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <FolderIcon accessible={!hostUnavailable && share.status === "active"} />
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-slate-900">
              {share.label || share.share_name}
            </div>
            <div className="mt-0.5 font-mono text-xs text-slate-500">{share.path}</div>
            <div className="mt-1 text-xs text-slate-400">{accessLabel} · for everyone in the office</div>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <ShareStatusBadge status={share.status} hostUnavailable={hostUnavailable} />
          {(share.status === "active" || share.status === "failed") && (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              className="no-tap-highlight text-xs font-medium text-slate-500 underline hover:text-slate-900 disabled:opacity-50"
            >
              {busy ? "Working…" : "Stop sharing"}
            </button>
          )}
        </div>
      </div>

      {hostUnavailable && share.status === "active" && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <WarningIcon />
          <span>
            <span className="font-medium">Not accessible right now</span> — this computer is off.
            Turn it on to open this folder.
          </span>
        </div>
      )}
    </li>
  );
}

function PrinterCard({
  printer,
  hostUnavailable,
  busy,
  onShare,
  onUnshare,
}: {
  printer: PrinterRow;
  hostUnavailable: boolean;
  busy: boolean;
  onShare: () => void;
  onUnshare: () => void;
}) {
  const isShared = printer.status === "active" || printer.status === "pending";
  const printerDisconnected =
    printer.connectivity === "offline" ||
    printer.connectivity === "error" ||
    printer.connectivity === "unplugged";
  const canPrint = printer.status === "active" && !hostUnavailable && !printerDisconnected;
  const canShare = (printer.status === "detected" || printer.status === "failed") && !printerDisconnected;

  return (
    <li className={`rounded-2xl border border-slate-200 bg-white p-5 ${(isShared && hostUnavailable) || printerDisconnected ? "opacity-70" : ""}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <PrinterIcon active={canPrint} />
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-slate-900">
              {printer.label || printer.printer_name}
            </div>
            {printer.driver && (
              <div className="mt-0.5 text-xs text-slate-500">{printer.driver}</div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <PrinterStatusBadge status={printer.status} connectivity={printer.connectivity} canPrint={canPrint} />
          {canShare && (
            <button
              type="button"
              onClick={onShare}
              disabled={busy}
              className="no-tap-highlight rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? "Working…" : "Share with the office"}
            </button>
          )}
          {isShared && (
            <button
              type="button"
              onClick={onUnshare}
              disabled={busy}
              className="no-tap-highlight text-xs font-medium text-slate-500 underline hover:text-slate-900 disabled:opacity-50"
            >
              {busy ? "Working…" : "Stop sharing"}
            </button>
          )}
        </div>
      </div>

      {printerDisconnected && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
          <WarningIcon />
          <span>
            <span className="font-medium">Printer not connected</span> — plug it in and turn it on,
            then it will appear as available.
          </span>
        </div>
      )}

      {isShared && hostUnavailable && !printerDisconnected && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <WarningIcon />
          <span>
            <span className="font-medium">Can't print right now</span> — this computer is off. Turn
            it on to use this printer.
          </span>
        </div>
      )}
    </li>
  );
}

function ShareStatusBadge({
  status,
  hostUnavailable,
}: {
  status: ShareRow["status"];
  hostUnavailable: boolean;
}) {
  if (status === "active") {
    return hostUnavailable ? (
      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
        Accessible when online
      </span>
    ) : (
      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-900">
        Accessible
      </span>
    );
  }
  const map: Partial<Record<ShareRow["status"], { label: string; className: string }>> = {
    pending: { label: "Setting up…", className: "bg-amber-100 text-amber-900" },
    failed: { label: "Setup failed", className: "bg-red-100 text-red-900" },
    removing: { label: "Removing…", className: "bg-slate-100 text-slate-600" },
  };
  const meta = map[status];
  if (!meta) return null;
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function PrinterStatusBadge({
  status,
  connectivity,
  canPrint,
}: {
  status: PrinterRow["status"];
  connectivity: PrinterRow["connectivity"];
  canPrint: boolean;
}) {
  if (connectivity === "unplugged") {
    return (
      <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
        Unplugged — plug it back in to use it
      </span>
    );
  }
  if (connectivity === "offline" || connectivity === "error") {
    return (
      <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
        Not connected
      </span>
    );
  }
  if (status === "active") {
    return canPrint ? (
      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-900">
        Ready to print
      </span>
    ) : (
      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
        Shared — host offline
      </span>
    );
  }
  const map: Partial<Record<PrinterRow["status"], { label: string; className: string }>> = {
    detected: { label: "Not shared", className: "bg-slate-100 text-slate-600" },
    pending: { label: "Setting up…", className: "bg-amber-100 text-amber-900" },
    failed: { label: "Setup failed", className: "bg-red-100 text-red-900" },
    removing: { label: "Removing…", className: "bg-slate-100 text-slate-600" },
  };
  const meta = map[status];
  if (!meta) return null;
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function SkeletonList({ count }: { count: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-5 w-5 animate-pulse rounded bg-slate-200" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/3 animate-pulse rounded bg-slate-200" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FolderIcon({ accessible }: { accessible: boolean }) {
  return (
    <svg
      className={`mt-0.5 h-5 w-5 shrink-0 ${accessible ? "text-emerald-500" : "text-slate-400"}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
      />
    </svg>
  );
}

function PrinterIcon({ active }: { active: boolean }) {
  return (
    <svg
      className={`mt-0.5 h-5 w-5 shrink-0 ${active ? "text-emerald-500" : "text-slate-400"}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.056 48.056 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z"
      />
    </svg>
  );
}

function ChevronLeft() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
        clipRule="evenodd"
      />
    </svg>
  );
}
